/**
 * R12 Office Distribution Matrix (Wallace v1, 2026-06-11)
 *
 * Per-assessment-area office placement vs. demographics:
 *   Assessment Area | Offices | #/% MMCTs in AA | #/% Offices in MMCT | #/% Offices in NMCT
 * with a TOTAL row. The "Offices in MMCT" cell is color-coded relative to the AA's MMCT
 * share: green when office placement keeps pace (≥), red when it lags, gray when the AA
 * has no MMCTs.
 */

import clsx from 'clsx';

interface R12OfficeDistributionRow {
  assessmentAreaName: string;
  isTotal: boolean;
  numOffices: number;
  mmctTracts: number;
  totalTracts: number;
  mmctTractPct: number;
  officesInMmct: number;
  officesInMmctPct: number;
  officesInNmct: number;
  officesInNmctPct: number;
  mmctRepresentation: 'favorable' | 'concern' | 'neutral';
}

interface R12MatrixProps {
  rows: R12OfficeDistributionRow[];
}

function fracPct(num: number, denom: number, pct: number): string {
  return `${num}/${denom} (${pct.toFixed(2)}%)`;
}

function countPct(num: number, pct: number): string {
  return `${num} (${pct.toFixed(2)}%)`;
}

function repClass(rep: R12OfficeDistributionRow['mmctRepresentation']): string {
  switch (rep) {
    case 'favorable': return 'text-emerald-700';
    case 'concern': return 'text-red-600';
    default: return 'text-ink-3';
  }
}

export function R12Matrix({ rows }: R12MatrixProps) {
  if (rows.length === 0) {
    return (
      <div className="rounded border border-dashed border-rule p-8 text-center">
        <p className="text-sm text-ink-3">No R12 office data available.</p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full border-collapse text-sm">
        <thead>
          <tr className="bg-panel-2 text-left text-xs font-medium uppercase tracking-wide text-ink-3">
            <th className="sticky left-0 z-10 bg-panel-2 px-3 py-2">Assessment Area</th>
            <th className="px-3 py-2 text-right">Offices</th>
            <th className="px-3 py-2 text-right">#/% MMCTs in AA</th>
            <th className="px-3 py-2 text-right">#/% Offices in MMCT</th>
            <th className="px-3 py-2 text-right">#/% Offices in NMCT</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(row => (
            <tr
              key={row.assessmentAreaName}
              className={clsx(
                'border-b border-rule',
                row.isTotal ? 'bg-panel-2 font-semibold' : 'hover:bg-panel-2',
              )}
            >
              <td className={clsx('sticky left-0 z-10 px-3 py-3 text-ink', row.isTotal ? 'bg-panel-2 font-semibold' : 'bg-panel font-medium')}>
                {row.assessmentAreaName}
              </td>
              <td className="px-3 py-3 text-right tabular-nums text-ink-2">
                {row.numOffices.toLocaleString()}
              </td>
              <td className="px-3 py-3 text-right tabular-nums text-ink-3">
                {fracPct(row.mmctTracts, row.totalTracts, row.mmctTractPct)}
              </td>
              <td className={clsx('px-3 py-3 text-right tabular-nums font-medium', repClass(row.mmctRepresentation))}>
                {row.isTotal
                  ? fracPct(row.officesInMmct, row.numOffices, row.officesInMmctPct)
                  : countPct(row.officesInMmct, row.officesInMmctPct)}
              </td>
              <td className="px-3 py-3 text-right tabular-nums text-ink-3">
                {row.isTotal
                  ? fracPct(row.officesInNmct, row.numOffices, row.officesInNmctPct)
                  : countPct(row.officesInNmct, row.officesInNmctPct)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="mt-3 text-xs text-ink-3">
        Offices = main office, branches, and LPOs (when applicable). &quot;#/% Offices in MMCT&quot; is
        <span className="text-emerald-700 font-medium"> green</span> when office placement meets or exceeds the area&apos;s
        MMCT share and <span className="text-red-600 font-medium">red</span> when it lags. NMCT = non-minority census tracts.
      </div>
    </div>
  );
}
